import type { CapabilitySwitch, StaffMember } from '@/features/admin/types'
import type { RoleOption } from '@/features/auth/types'
import { isSwitchAllowed } from '@/hooks/useSwitch'

/**
 * Search by name or e-mail, and narrow to the picked area: a Standort, or a
 * franchise together with its Standorte (`nodeIds`, `null` for all).
 */
export function filterStaff(
  items: StaffMember[],
  query: string,
  nodeIds: ReadonlySet<string> | null,
): StaffMember[] {
  const needle = query.trim().toLocaleLowerCase('de')
  return items.filter((member) => {
    const matchesQuery =
      needle === '' ||
      member.displayName.toLocaleLowerCase('de').includes(needle) ||
      member.email.toLocaleLowerCase('de').includes(needle)
    const matchesNode =
      nodeIds === null ||
      member.assignments.some((assignment) => nodeIds.has(assignment.node.publicId))
    return matchesQuery && matchesNode
  })
}

/** `canManage` is the server's word; the UI only adds "never yourself". */
export function canBlock(member: StaffMember, selfId: string | null): boolean {
  return member.canManage && member.publicId !== selfId && member.status === 'active'
}

export function canUnblock(member: StaffMember, selfId: string | null): boolean {
  return member.canManage && member.publicId !== selfId && member.status === 'disabled'
}

/** The switch that gates inviting each Standort role (cascading-rights plan). */
const INVITE_SWITCH: Record<string, string> = {
  branch_staff: 'INVITE_STAFF',
  branch_manager: 'INVITE_MANAGER',
}

/** The roles still on offer once the Standort's invite switches are applied. */
export function invitableRoles(
  roles: RoleOption[],
  switches: Record<string, boolean> | undefined,
): RoleOption[] {
  return roles.filter((role) => {
    const capability = INVITE_SWITCH[role.key]
    return capability === undefined || isSwitchAllowed(switches, capability)
  })
}

/** Role keys a Standort refuses, from its switch list (effective: own and franchise's). */
export function deniedInviteRoles(switches: CapabilitySwitch[] | undefined): string[] {
  if (!switches) return []
  const effective = Object.fromEntries(
    switches.map((item) => [item.capability, item.allowed && item.allowedAbove]),
  )
  return Object.entries(INVITE_SWITCH)
    .filter(([, capability]) => !isSwitchAllowed(effective, capability))
    .map(([role]) => role)
}
